import React, { useEffect, useState } from "react";
import { Link, NavLink, Navigate, Outlet, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard,
  Car,
  MessageSquare,
  Images,
  Settings,
  Send,
  Bell,
  LogOut,
  Menu,
  X,
} from "lucide-react";
import { CONTACT, IMAGES } from "@/config";
import { supabase } from "@/lib/supabase";

const nav = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/listings", label: "Listings", icon: Car },
  { to: "/admin/leads", label: "Leads", icon: MessageSquare },
  { to: "/admin/media", label: "Media", icon: Images },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

export const AdminLayout: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const verify = async (session: unknown) => {
      if (!session) {
        setAuthenticated(false);
        return;
      }
      const { data } = await supabase.rpc("is_inventory_admin");
      setAuthenticated(data === true);
    };
    supabase.auth.getSession().then(({ data }) => verify(data.session));
    const { data } = supabase.auth.onAuthStateChange((_event, session) => { void verify(session); });
    return () => data.subscription.unsubscribe();
  }, []);

  const logout = async () => {
    await supabase.auth.signOut();
    toast.success("You've been logged out.");
    navigate("/admin/login");
  };

  if (authenticated === null) return <div className="flex min-h-screen items-center justify-center bg-slate-50 text-slate-500">Checking secure access...</div>;
  if (!authenticated) return <Navigate to="/admin/login" replace />;

  return (
    <div className="flex min-h-screen bg-slate-50">
      {/* Overlay (mobile) */}
      {open && (
        <div className="fixed inset-0 z-30 bg-slate-900/40 lg:hidden" onClick={() => setOpen(false)} />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 flex w-64 shrink-0 flex-col border-r border-slate-200 bg-white transition-transform duration-200 lg:static lg:translate-x-0 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between border-b border-slate-100 p-5">
          <Link to="/admin" className="flex items-center gap-2.5 leading-tight" onClick={() => setOpen(false)}>
            <img src={IMAGES.brandLogo} alt="" className="h-10 w-10 rounded-lg object-contain" width="40" height="40" />
            <div>
              <div className="text-lg font-extrabold text-slate-900">
                Simphiwe <span className="text-red-600">Fudwana</span>
              </div>
              <div className="text-[11px] text-slate-500">{CONTACT.role}</div>
            </div>
          </Link>
          <button className="lg:hidden" onClick={() => setOpen(false)} aria-label="Close menu">
            <X className="h-5 w-5 text-slate-500" />
          </button>
        </div>

        <nav className="flex-1 space-y-1 p-3">
          {nav.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                onClick={() => setOpen(false)}
                data-testid={`admin-nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
                className={({ isActive }) =>
                  `flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors ${
                    isActive ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-50"
                  }`
                }
              >
                <Icon className="h-4 w-4" /> {n.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="space-y-1 border-t border-slate-100 p-3">
          <Link
            to="/"
            className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold text-blue-600 hover:bg-blue-50"
          >
            <Send className="h-4 w-4" /> View Website
          </Link>
          <button
            onClick={logout}
            data-testid="admin-logout-sidebar"
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold text-red-600 hover:bg-red-50"
          >
            <LogOut className="h-4 w-4" /> Log Out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex items-center justify-between border-b border-slate-200 bg-white px-5 py-3.5">
          <button className="lg:hidden" onClick={() => setOpen(true)} aria-label="Open menu">
            <Menu className="h-6 w-6 text-slate-600" />
          </button>
          <div className="ml-auto flex items-center gap-4">
            <button className="relative text-slate-500" aria-label="Notifications">
              <Bell className="h-5 w-5" />
            </button>
            <div className="hidden items-center gap-2 sm:flex">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-600 text-sm font-bold text-white">SF</span>
              <div className="leading-tight">
                <div className="text-xs font-bold text-slate-900">Admin</div>
                <div className="text-[11px] text-slate-500">Simphiwe Fudwana</div>
              </div>
            </div>
            <button
              onClick={logout}
              data-testid="admin-logout-header"
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-600"
            >
              <LogOut className="h-4 w-4" /> Log Out
            </button>
          </div>
        </header>

        <div className="p-5">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;
